import { useMemo } from 'react';
import SplitLayout from '@/modules/onboarding/components/split-layout';
import FillMedboxPic from '@/assets/images/medbox/fill-medbox.svg';
import Pills from '@/assets/images/medbox/pills.svg';
import PillBottle from '@/assets/images/medbox/pill-bottle.svg';
import PillsPic from '@/assets/icons/pills.svg';
import { ReactNode } from 'react';
import { useLocation } from 'react-router';
import { copayEligiblePotencyCodes } from '@/shared/constants/potency-code';
import { ROUTES } from '@/shared/constants/routes';

import NonPillCapsuleQuantity from '@/assets/images/onboarding/add_medication_flow/non-pill-capsule-quantity.svg';

interface IProps {
    dataTestid?: string;
    prescriptionsLength: number;
    mainContent: ReactNode;
    extraContent?: ReactNode;
    otcAsRx: boolean | null;
    selectedMedication: any;
    packaging?: string | null;
}

const NowLetsStartFillingYourMedBox = () => (
    <div className="flex flex-col items-center gap-1 xl:items-start xl:gap-9">
        <h1 className="prescriptions__heading text-center font-agrandir text-[3.125rem] xl:text-left sm-max:!text-[1.5rem] xl-max:text-[2.5rem] leading-[130%]">
            Now let’s <span className="text-primary-700">Start Filling Your MedBox</span> by adding your medications.
        </h1>
        <img src={FillMedboxPic} className="hidden h-[277px] w-[326px] xl:block" />
    </div>
)

export const DidYouKnowPills = ({ customClasses }: { customClasses?: string }) => (
    <div className={`relative max-w-[440px] w-full rounded-xl bg-mint-400 p-6 ${customClasses}`}>
        <span className="font-atkinsonHyperlegible text-18 xl:text-25">
            Did you know we will work with your previous pharmacy to{' '}
            <span className="font-bold italic">
                Transfer
                <br /> Your Prescriptions
            </span>{' '}
            to MedBox?
        </span>
        <img src={PillsPic} className="absolute -right-[12px] bottom-[-60px] z-10 w-[96px]" alt="" />
    </div>
)

export const Tip = ({ hasImage = true, customClasses }: { hasImage?: boolean, customClasses?: string }) => (
    <div className={`relative max-w-[440px] w-full rounded-xl bg-mint-400 p-6 ${customClasses}`}>
        <span className="font-atkinsonHyperlegible text-[18px] sm:text-[25px] leading-[130%] text-[#202524]">
            Tip: Check your current{' '}
            <span className="font-atkinsonBold font-bold italic">
                medication label
            </span>
            {' '}to see the quantity (QTY)
        </span>
        {hasImage && <img src={NonPillCapsuleQuantity} className="hidden xl:block translate-y-[-65px] z-10 max-w-[300px]" alt="" />}
    </div >
)

export const OtcPillTip = ({ hasImage = true, customClasses }: { hasImage?: boolean, customClasses?: string }) => (
    <div className={`relative max-w-[380px] w-full rounded-xl bg-mint-400 p-[25px] ${customClasses}`}>
        <span className="font-atkinsonHyperlegible text-[18px] sm:text-[25px] leading-[130%] text-[#202524]">
            Simplify your routine! Include your OTC medications in your{' '}
            <span className="font-atkinsonBold font-bold">
                medication roll
            </span>
            {/* ? {' '} Include your OTC medications in your{' '}
            <span className="font-atkinsonBold font-bold">
                medication roll
            </span> */}
        </span>
        {hasImage && <img src={Pills} className="xl:absolute -right-[12px] bottom-[-60px] z-10 w-[128px]" alt="" />}
    </div>
)

export const NonPillQuantityTip = ({ hasImage = true, customClasses }: { hasImage?: boolean, customClasses?: string }) => (
    <div className={`relative max-w-[369px] w-full rounded-xl bg-mint-400 p-[25px] ${customClasses}`}>
        <span className="font-atkinsonHyperlegible text-[18px] sm:text-[25px] leading-[130%] text-[#202524]">
            Tip: Look for the <span className="font-atkinsonBold font-bold italic">quantity or volume</span> on your item label
        </span>
        {hasImage && <img src={NonPillCapsuleQuantity} className="hidden xl:block absolute -left-[12px] bottom-[-330px] z-10 w-[357px]" alt="" />}
    </div>
)


const YoureDoingAFantasticJobFillingYourMedBox = () => (
    <div className="flex flex-col items-center gap-[15px] xl:gap-[20px] xl:items-start">
        <h2 className="block xl:hidden prescriptions__heading text-center font-agrandir text-25 leading-[130%]">
            <span className="text-primary-700">Add Medications</span> to your MedBox
        </h2>
        <h2 className="hidden xl:block prescriptions__heading text-center font-agrandir text-[3.125rem] xl:text-left sm-max:!text-[1.5rem] xl-max:text-[2.5rem] leading-[130%]">
            {"You're doing a fantastic job filling your MedBox. "}
            <span className="text-primary-700">Keep it up!</span>
        </h2>
        <div className="relative hidden xl:block">
            <DidYouKnowPills customClasses='rounded-tr-none' />
        </div>
    </div>
)




const OtcPillsMedbox = () => {
    return (
        (
            <div className="flex flex-col items-center gap-[15px] xl:gap-[20px] xl:items-start">
                <img src={Pills} className="block xl:hidden w-[128px]" alt="" />
                <h2 className="block prescriptions__heading text-center font-agrandir text-[3.125rem] xl:text-left sm-max:!text-[1.5rem] xl-max:text-[2.5rem] leading-[130%]">
                    Tell us{' '}
                    <span className="text-primary-700">what quantity</span> you need
                </h2>

                <div className="relative hidden xl:block">
                    <OtcPillTip customClasses='rounded-tr-none' />
                </div>
            </div>
        )
    )
}

export const OtcPillTipSeparate = ({ hasImage = true, customClasses }: { hasImage?: boolean, customClasses?: string }) => (
    <div className={`relative max-w-[380px] w-full rounded-xl bg-mint-400 p-[25px] ${customClasses}`}>
        <span className="font-atkinsonHyperlegible text-[18px] sm:text-[25px] leading-[130%] text-[#202524]">
           Tip: Check your current{' '}
            <span className="font-atkinsonBold font-bold">
                medication label{' '}
            </span>
            <span>to see the quantity (QTY)</span>
        </span>
        {hasImage && <img src={PillBottle} className="xl:absolute right-[30px] bottom-[-250px] z-10 w-[197px] h-[278px]" alt="" />}
    </div>
)


const OtcPillsSeparate = () => 
    (
        <div className="flex flex-col items-center gap-[15px] xl:gap-[20px] xl:items-start">
            <img src={PillBottle} className="block xl:hidden w-[60px]" alt="" />
            <h2 className="block prescriptions__heading text-center font-agrandir text-[3.125rem] xl:text-left sm-max:!text-[1.5rem] xl-max:text-[2.5rem] leading-[130%]">
                Tell us{' '}
                <span className="text-primary-700">what quantity</span> you need
            </h2>

            <div className="relative hidden xl:block">
                <OtcPillTipSeparate customClasses='rounded-tr-none' />
            </div>
        </div>
    )

const OtcPillsLeft = ({ packaging }: { packaging?: string | null }) => {
    if (packaging === 'Include in my MedBox') {
        return (
            <OtcPillsMedbox packaging={packaging} />
        )
    }  
    return (
        <OtcPillsSeparate />
    )
}

export const WhatQuantityNonTabletNonRxMedication = ({ customClasses }: { customClasses?: string }) => (
                <div className='flex flex-col items-center xl:items-start gap-[7px] xl:gap-[20px]'>
                    <img src={NonPillCapsuleQuantity} className="block xl:hidden max-w-[168px]" alt="" />
                    <h2 className="block prescriptions__heading text-center xl:text-left font-agrandir text-[25px] xl:text-[50px] leading-[130%] text-black">
                        Tell us{' '}
                        <span className="text-primary-700">the quantity</span> listed on the labeling
                    </h2>
                    <NonPillQuantityTip customClasses={`hidden xl:block ${customClasses || ''} rounded-bl-none`} />
                </div>
            )

        const WhatQuantityNonCapsuleRxMedicationContent = () => (
            <div className="flex flex-col items-center sm-max:gap-[0.4375rem] xl:items-start">
                <img src={NonPillCapsuleQuantity} className="block max-w-[300px] xl:hidden px-[50px]" />
                <h2 className="prescriptions__heading text-center font-agrandir text-[3.125rem] xl:text-left sm-max:!text-[1.5rem] xl-max:text-[2.5rem] leading-[130%]">
                    Tell us <span className="text-primary-700">what quantity</span>
                    <span className="xs:hidden">{' '}</span>
                    <br className="" />
                    you need
                </h2>
                <img src={NonPillCapsuleQuantity} className="hidden xl:block z-10 max-w-[300px]" alt="" />
            </div>
        )


        const AddMedicationLayout = ({ prescriptionsLength, mainContent, extraContent, dataTestid, otcAsRx, selectedMedication, packaging }: IProps) => {
            const location = useLocation();
            const path = location.pathname;
            const isPillOrCapsule = useMemo(() => {
                return selectedMedication && copayEligiblePotencyCodes.has(selectedMedication.prescribable.potencyCode);
            }, [selectedMedication]);


            const leftSideContent = useMemo(() => {
                if (path.includes(ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.ADD_MEDICATION)) {
                    return (
                        prescriptionsLength ? (
                            <YoureDoingAFantasticJobFillingYourMedBox />
                        ) : (
                            <NowLetsStartFillingYourMedBox />
                        )
                    )
                } else if (path.includes(ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.MEDICATION_QUANTITY_AS_OTC)) {
                    if (!isPillOrCapsule) {
                        return <WhatQuantityNonTabletNonRxMedication />;
                    }
                    return <OtcPillsLeft packaging={packaging} />;
                } else if (path.includes(ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.MEDICATION_QUANTITY_AS_RX)) {
                    if (!isPillOrCapsule) {
                        return <WhatQuantityNonCapsuleRxMedicationContent />;
                    }
                    return <YoureDoingAFantasticJobFillingYourMedBox />;
                }
                return <NowLetsStartFillingYourMedBox />
            }, [isPillOrCapsule, path, prescriptionsLength, packaging]);

            
            const isOtcQuantityPath = path.includes(ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.MEDICATION_QUANTITY_AS_OTC);

            return (
                <SplitLayout
                    dataTestid={dataTestid}
                    className={isOtcQuantityPath ? '!mb-[80px]' : undefined}
                    sideContent={
                        <div className="flex max-w-[575px] xl:mr-12 3xl:mr-[12rem] xl-max:py-[20px]">
                            {leftSideContent}
                        </div>
                    }
                    mainContent={mainContent}
                    extraContent={extraContent}
                />
            );
        };

        export default AddMedicationLayout;
